import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Heart,
  Menu,
  Play,
  ShieldCheck,
  X,
} from 'lucide-react';
import Auth from '../auth/Auth';
import { JsonLd, breadcrumbJsonLd } from '../../seo/JsonLd';
import { BASE_URL } from '../../seo/types';
import { getMiraPlusAppUrl } from '../../lib/miraplusAppUrl';
import '../landing/TinyWinsHomeLanding.css';
import './AboutUs.css';

const GROWTH_AREAS = [
  'Communication',
  'Independence',
  'Emotional regulation',
  'Daily living',
  'Social connection',
  'Learning',
  'Confidence',
  'Participation',
] as const;

const WIN_EXAMPLES = [
  'trying a new food',
  'asking for help',
  'tolerating a transition',
  'saying a new word',
  'brushing teeth independently',
  'making eye contact with someone they love',
] as const;

const AboutUs: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signIn' | 'signUp'>('signUp');

  const openAuth = (mode: 'signIn' | 'signUp') => {
    setAuthMode(mode);
    setAuthOpen(true);
    setMenuOpen(false);
  };

  return (
    <div className="tw-home tw-about">
      <JsonLd
        data={breadcrumbJsonLd([
          { name: 'Home', url: BASE_URL },
          { name: 'About', url: `${BASE_URL}/about` },
        ])}
      />

      <header className="tw-home-nav">
        <div className="tw-home-shell tw-home-nav-inner">
          <Link to="/" className="tw-home-logo" aria-label="TinyWins.ai home">
            <img
              className="tw-home-logo-img"
              src="/images/TinyWinsLogo.jpeg"
              alt="TinyWins.ai"
              width={150}
              height={40}
            />
          </Link>

          <nav className="tw-home-nav-links" aria-label="Primary">
            <a href={getMiraPlusAppUrl()}>Therapists</a>
            <Link to="/schools-and-counties">Schools &amp; Counties</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/resources">Resources</Link>
            <Link to="/about" className="is-active">
              About
            </Link>
          </nav>

          <div className="tw-home-nav-actions">
            <button type="button" className="tw-home-link-btn" onClick={() => openAuth('signIn')}>
              Sign In
            </button>
            <button type="button" className="tw-btn tw-btn-coral tw-btn-sm" onClick={() => openAuth('signUp')}>
              Start Free
            </button>
            <button
              type="button"
              className="tw-home-menu-btn"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <div className={`tw-home-shell tw-home-mobile-panel${menuOpen ? ' is-open' : ''}`}>
          <a href={getMiraPlusAppUrl()} onClick={() => setMenuOpen(false)}>
            Therapists
          </a>
          <Link to="/schools-and-counties" onClick={() => setMenuOpen(false)}>
            Schools &amp; Counties
          </Link>
          <Link to="/how-it-works" onClick={() => setMenuOpen(false)}>
            How It Works
          </Link>
          <Link to="/resources" onClick={() => setMenuOpen(false)}>
            Resources
          </Link>
          <Link to="/about" onClick={() => setMenuOpen(false)}>
            About
          </Link>
          <button type="button" onClick={() => openAuth('signIn')}>
            Sign In
          </button>
          <button type="button" onClick={() => openAuth('signUp')}>
            Start Free
          </button>
        </div>
      </header>

      <main>
        <section className="tw-about-hero" aria-label="Our story">
          <div className="tw-about-hero-media">
            <img
              className="tw-about-hero-photo"
              src="/images/about/vk-ak-snow.webp"
              alt="Vikrant holding Arnav in the snow, both smiling"
              width={1905}
              height={972}
            />
          </div>
          <div className="tw-about-hero-overlay" />
          <div className="tw-home-shell tw-about-hero-copy">
            <span className="tw-about-kicker">
              <Heart size={14} fill="currentColor" aria-hidden />
              Born from one family&apos;s journey
            </span>
            <h1>
              Big progress begins with <span className="tw-about-hero-accent">Tiny Wins.</span>
            </h1>
            <p className="tw-about-hero-lead">
              TinyWins was built by a father who spent years watching his son work toward milestones
              most families never have to think about. Today, it helps parents break overwhelming
              goals into smaller steps, recognize meaningful progress, and work more closely with
              the people supporting their child.
            </p>
            <div className="tw-about-hero-ctas">
              <button type="button" className="tw-btn tw-btn-coral tw-btn-pill" onClick={() => openAuth('signUp')}>
                Start with TinyWins — Free
                <span className="tw-btn-icon-circle tw-btn-icon-circle--light" aria-hidden>
                  <ArrowRight size={14} strokeWidth={2.5} />
                </span>
              </button>
              <Link to="/how-it-works" className="tw-btn tw-btn-ghost-light tw-btn-pill">
                See How TinyWins Works
                <span className="tw-btn-icon-circle tw-btn-icon-circle--onteal" aria-hidden>
                  <Play size={12} fill="currentColor" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        <section id="story" className="tw-about-section tw-about-section--soft">
          <div className="tw-home-shell">
            <h2>One milestone changed everything</h2>
            <p className="tw-about-lede">
              TinyWins was not created in a boardroom. It was born from one family&apos;s journey.
            </p>

            <div className="tw-about-story-grid">
              <div className="tw-about-photo-frame">
                <img
                  src="/images/about/ak-ak-feeding.webp"
                  alt="Anjali feeding Arnav at home, with family looking on"
                  width={920}
                  height={880}
                />
                <p className="tw-about-photo-caption">Anjali at home with Arnav — practice, every day</p>
              </div>

              <div className="tw-about-timeline">
                <div className="tw-about-timeline-item">
                  <p className="tw-about-timeline-label">Age 4</p>
                  <h3>Intensive feeding therapy</h3>
                  <p>
                    Arnav attended Kennedy Krieger Institute&apos;s intensive feeding program after
                    years of difficulty eating and chewing. The family was told chewing might remain
                    a major long-term challenge.
                  </p>
                </div>
                <div className="tw-about-timeline-item">
                  <p className="tw-about-timeline-label">The years that followed</p>
                  <h3>Thousands of tiny attempts</h3>
                  <p>
                    Therapy, home practice, frustration, encouragement, repetition and small
                    improvements that were often difficult to notice day to day.
                  </p>
                </div>
                <div className="tw-about-timeline-item">
                  <p className="tw-about-timeline-label">Years later</p>
                  <h3>Arnav learned to chew</h3>
                  <p>What once seemed impossible eventually became part of everyday life.</p>
                </div>
                <div className="tw-about-timeline-item">
                  <p className="tw-about-timeline-label">The realization</p>
                  <h3>Progress rarely happens in one giant leap.</h3>
                  <p>It happens through tiny wins.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="tw-about-section">
          <div className="tw-home-shell tw-about-quote-grid">
            <blockquote className="tw-about-quote">
              <span className="tw-about-quote-mark" aria-hidden>
                “
              </span>
              <p>
                As a parent, I often wished someone could show me the small steps between where my
                son was and where we wanted him to be.
              </p>
              <p>We spent years working toward goals that sometimes felt impossible.</p>
              <p>
                Looking back, the progress was there all along. We just didn&apos;t always know how
                to see it.
              </p>
              <p>TinyWins was created to help other families see those steps sooner.</p>
              <footer>
                <cite>— Vikrant Kulkarni</cite>
                <span>Father · Technologist · Founder of TinyWins.ai</span>
              </footer>
            </blockquote>
            <img
              className="tw-about-quote-photo"
              src="/images/about/vk-ak-snow-2.jpg"
              alt="Vikrant holding Arnav in the snow, both smiling"
              width={1200}
              height={1500}
            />
          </div>
        </section>

        <section className="tw-about-section tw-about-section--soft">
          <div className="tw-home-shell tw-about-values-grid">
            <div className="tw-about-values-col">
              <h2>Every child deserves to be seen for more than a diagnosis</h2>
              <p className="tw-about-lede">
                TinyWins isn&apos;t about comparing children or chasing a definition of “normal.” It
                is about helping families recognize growth in:
              </p>
              <div className="tw-about-chips">
                {GROWTH_AREAS.map((area) => (
                  <span key={area} className="tw-about-chip">
                    {area}
                  </span>
                ))}
              </div>
              <div className="tw-about-win-card">
                <p>A win can be huge.</p>
                <p>A win can also be:</p>
                <div className="tw-about-win-pills">
                  {WIN_EXAMPLES.map((example) => (
                    <span key={example}>{example}</span>
                  ))}
                </div>
                <div className="tw-about-win-close">
                  <p>Small to the world.</p>
                  <strong>Huge to a family.</strong>
                </div>
              </div>
            </div>

            <div className="tw-about-values-col tw-about-tech">
              <div className="tw-about-tech-icon">
                <ShieldCheck size={26} aria-hidden />
              </div>
              <h2>Technology should support families — not replace people</h2>
              <p>
                TinyWins uses AI to help organize information, suggest possible next steps and make
                complex information easier for parents to understand.
              </p>
              <p className="tw-about-tech-note">
                TinyWins does not replace therapists, doctors, educators, or professional judgment.
              </p>
              <p>
                The technology is intended to help families become more informed, organized and
                engaged participants in their child&apos;s journey.
              </p>
            </div>
          </div>
        </section>

        <section id="founder" className="tw-about-section">
          <div className="tw-home-shell tw-about-founder">
            <img
              src="/images/about/family-2014.webp"
              alt="Anjali, Vikrant, and Arnav together at home"
              width={733}
              height={720}
            />
            <div className="tw-about-founder-copy">
              <h2>Meet Vikrant</h2>
              <p className="tw-about-founder-name">Vikrant Kulkarni</p>
              <p className="tw-about-founder-role">Founder, TinyWins.ai</p>
              <p>
                Vikrant is a technology architect, entrepreneur and father. Together with Anjali, he
                spent years supporting their son Arnav through autism, feeding therapy, education and
                developmental milestones — the experience that inspired him to build TinyWins.
              </p>
              <blockquote className="tw-about-founder-quote">
                Parents shouldn&apos;t have to wait years to realize how much progress their child
                has made.
              </blockquote>
            </div>
          </div>
        </section>

        <section className="tw-home-shell tw-about-cta-band" aria-label="Get started">
          <div className="tw-home-cta-band">
            <div className="tw-home-cta-photo-wrap">
              <img
                className="tw-home-cta-photo"
                src="/images/about/ak-first-bday.jpg"
                alt=""
                width={480}
                height={520}
                loading="lazy"
              />
            </div>
            <div className="tw-home-cta-copy">
              <h2>Your child&apos;s next big milestone may begin with a very small step.</h2>
              <p>Start noticing those steps today.</p>
              <div className="tw-home-cta-actions">
                <button type="button" className="tw-btn tw-btn-coral tw-btn-pill" onClick={() => openAuth('signUp')}>
                  Start TinyWins for Free
                  <span className="tw-btn-icon-circle tw-btn-icon-circle--oncoral" aria-hidden>
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </span>
                </button>
              </div>
              <p>
                Track up to 3 goals, explore Goal Coach, analyze an IEP and access parent resources
                with the free TinyWins plan.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Auth isOpen={authOpen} onClose={() => setAuthOpen(false)} initialAuthState={authMode} />
    </div>
  );
};

export default AboutUs;
